import { motion, useInView } from "framer-motion";
import { useState, useRef } from "react";
import { Sparkles, Crown, ArrowRight, Image as ImageIcon } from "lucide-react";
import { PILLARS } from "./data";

const ease = [0.16, 1, 0.3, 1];

const goBook = (prefill) => {
  window.dispatchEvent(new CustomEvent("tpj-prefill", { detail: prefill }));
  document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
};

const PillarMedia = ({ p }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-25% 0px -25% 0px" });
  const [showImage, setShowImage] = useState(false);

  if (!p.flipList && !p.flipGroups) {
    return (
      <div className="relative aspect-[4/3] overflow-hidden group">
        <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute top-4 left-4 bg-pine text-bone px-3 py-1 text-xs tracking-[0.15em]">{p.n}</span>
      </div>
    );
  }

  // Default (while scrolled into view) = workshops text; hover/tap = image
  const textFacing = inView && !showImage;

  return (
    <div
      ref={ref}
      className="relative aspect-[3/4] sm:aspect-[4/3] overflow-hidden cursor-pointer"
      style={{ perspective: 1400 }}
      data-testid={`service-flip-${p.n}`}
      onMouseEnter={() => setShowImage(true)}
      onMouseLeave={() => setShowImage(false)}
      onClick={() => setShowImage((s) => !s)}
    >
      <motion.div
        className="relative w-full h-full"
        animate={{ rotateY: textFacing ? 180 : 0 }}
        transition={{ duration: 0.7, ease }}
        style={{ transformStyle: "preserve-3d" }}
      >
        <div className="absolute inset-0 overflow-hidden" style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}>
          <img src={p.img} alt={p.title} className="w-full h-full object-cover" />
          <span className="absolute top-4 left-4 bg-pine text-bone px-3 py-1 text-xs tracking-[0.15em]">{p.n}</span>
        </div>
        <div
          className="absolute inset-0 bg-pine text-bone p-5 md:p-8 flex flex-col justify-center overflow-hidden"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
        >
          <p className="font-serif text-base md:text-2xl mb-3 md:mb-4 text-terra pr-4 sm:pr-24">{p.flipTitle}</p>
          {p.flipGroups ? (
            <div className="grid grid-cols-2 gap-x-4 md:gap-x-5 gap-y-2 md:gap-y-3">
              {p.flipGroups.map((g) => (
                <div key={g.heading}>
                  <p className="font-bold text-xs md:text-base mb-1 md:mb-1.5">{g.heading}</p>
                  <ul className="space-y-1 text-[0.62rem] leading-[1.2] md:text-xs md:leading-tight text-bone/85">
                    {g.items.map((x) => (
                      <li key={x} className="flex gap-1"><span className="text-terra shrink-0">—</span>{x}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1 text-[0.72rem] leading-[1.2] md:text-sm md:leading-tight text-bone/85">
              {p.flipList.map((x) => (
                <li key={x} className="flex gap-1.5"><span className="text-terra shrink-0">—</span>{x}</li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-[0.6rem] md:text-xs text-bone/50 italic">* Additional topics and customised workshops are available on request</p>
          <span className="hidden sm:flex absolute top-4 right-4 items-center gap-1.5 bg-terra text-white px-3 py-1.5 text-xs tracking-wide">
            <ImageIcon className="w-3.5 h-3.5" /> Hover / tap for photo
          </span>
        </div>
      </motion.div>
    </div>
  );
};

export const Services = () => (
  <section id="services" className="relative bg-sand/50 py-24 md:py-36 overflow-hidden" data-testid="services">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
        <div className="max-w-xl">
          <p className="text-sm md:text-base tracking-[0.22em] uppercase font-semibold text-terra mb-3">What we do</p>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">Workshops<span className="text-terra">.</span></h2>
          <p className="mt-3 font-serif text-xl md:text-2xl italic text-ink/70">Support that meets your school where it is.</p>
        </div>
        <div className="max-w-sm">
          <p className="text-ink/65 md:text-lg">
            Delivered on-site or online, always co-designed with your leadership and shaped around your priorities.
          </p>
          <p className="mt-3 font-serif italic font-bold text-terra text-lg md:text-xl">No fixed package. No one-size-fits-all approach.</p>
        </div>
      </div>

      <div className="space-y-16 md:space-y-28">
        {PILLARS.map((p, i) => {
          const flip = i % 2 === 0;
          return (
            <motion.div
              key={p.n}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease }}
              data-testid={`service-${p.n}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center"
            >
              <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                <PillarMedia p={p} />
              </div>
              <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                <p className="font-serif italic text-terra text-xl md:text-2xl mb-3">{p.tagline}</p>
                <h3 className="font-serif text-4xl md:text-5xl leading-tight mb-6">{p.title}</h3>
                <ul className="space-y-3">
                  {p.points.map((pt, idx) => {
                    const boldLast = idx === p.points.length - 1 && !p.highlight;
                    return (
                      <li key={pt} className={`flex items-start gap-3 md:text-lg ${boldLast ? "font-bold text-ink" : "text-ink/75"}`}>
                        <span className="mt-1.5 w-5 h-5 shrink-0 bg-terra/15 flex items-center justify-center">
                          <Sparkles className="w-3.5 h-3.5 text-terra" strokeWidth={2} />
                        </span>
                        {pt}
                      </li>
                    );
                  })}
                  {p.highlight && (
                    <li className="flex items-start gap-3 font-bold text-ink md:text-lg">
                      <span className="mt-1.5 w-5 h-5 shrink-0 bg-terra/25 flex items-center justify-center">
                        <Crown className="w-3.5 h-3.5 text-terra" strokeWidth={2.5} />
                      </span>
                      {p.highlight}
                    </li>
                  )}
                </ul>
                {p.cta && (
                  <button
                    onClick={() => goBook(p.cta.prefill)}
                    data-testid={`service-cta-${p.n}`}
                    className="group mt-8 inline-flex items-center gap-2.5 bg-terra text-white px-6 py-3.5 text-sm tracking-wide hover:bg-ink transition-colors duration-300"
                  >
                    {p.cta.label}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);
